import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalOrderFormData, createPortalOrder } from "@/lib/portal.functions";
import { listCustomFields } from "@/lib/custom-fields.functions";
import { CustomFieldsForm, type CustomFieldDef } from "@/components/custom-fields/dynamic-fields";
import { validateCustomFields } from "@/lib/orders/custom-field-validate";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/orders/new")({
  component: PortalOrderNew,
});

type Line = { skuId: string; qty: number };

function PortalOrderNew() {
  const navigate = useNavigate();
  const fetchForm = useServerFn(getPortalOrderFormData);
  const submitFn = useServerFn(createPortalOrder);
  const fetchCustomFields = useServerFn(listCustomFields);
  const { data, isLoading } = useQuery({
    queryKey: ["portal-order-form"],
    queryFn: () => runServerFn(() => fetchForm(), "Loading form"),
  });
  const tenantId = data?.customer?.tenant_id;
  const { data: cfData } = useQuery({
    queryKey: ["portal-order-custom-fields", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(
      () => fetchCustomFields({ data: { tenantId: tenantId!, entity: "orders", enabledOnly: true } }),
      "Loading custom fields",
    ),
  });
  const cfDefs = (cfData?.rows ?? []) as CustomFieldDef[];

  const [orderNumber, setOrderNumber] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [carrier, setCarrier] = useState(""); const [service, setService] = useState("");
  const [notes, setNotes] = useState("");
  const [shipTo, setShipTo] = useState({
    name: "", company: "", street1: "", street2: "",
    city: "", state: "", postal_code: "", country: "US", phone: "", email: "",
  });
  const [lines, setLines] = useState<Line[]>([{ skuId: "", qty: 1 }]);
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);

  const updateLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines((p) => [...p, { skuId: "", qty: 1 }]);
  const removeLine = (i: number) => setLines((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.customer) return toast.error("No customer profile");
    if (!facilityId) return toast.error("Pick a facility");
    const cleanLines = lines.filter((l) => l.skuId && l.qty > 0);
    if (cleanLines.length === 0) return toast.error("Add at least one line");
    const cfErrors = validateCustomFields(cfDefs, customFields);
    const firstErr = Object.values(cfErrors)[0];
    if (firstErr) return toast.error(firstErr);
    setSubmitting(true);
    try {
      await runServerFn(() => submitFn({
        data: {
          tenantId: data.customer!.tenant_id,
          customerId: data.customer!.id,
          facilityId,
          orderNumber: orderNumber.trim(),
          shipTo: {
            ...shipTo,
            company: shipTo.company || null,
            street2: shipTo.street2 || null,
            phone: shipTo.phone || null,
            email: shipTo.email || null,
          },
          carrier: carrier || null,
          serviceLevel: service || null,
          notes: notes || null,
          lines: cleanLines,
          customFields,
        },
      }), "Creating order");
      toast.success("Order created");
      navigate({ to: "/portal/orders" });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="page-container py-12 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <>
      <PortalPageHeader title="New Order" description="Submit an outbound order for fulfillment." />
      <div className="page-container">
        <form onSubmit={submit} className="space-y-6 max-w-3xl">
          <Card className="p-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Order number</Label>
                <Input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="PO-1001" />
              </div>
              <div className="space-y-2">
                <Label>Facility</Label>
                <Select value={facilityId} onValueChange={setFacilityId}>
                  <SelectTrigger><SelectValue placeholder="Pick facility" /></SelectTrigger>
                  <SelectContent>
                    {data?.facilities?.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Carrier (optional)</Label>
                <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS / FedEx / DHL" />
              </div>
              <div className="space-y-2">
                <Label>Service type (optional)</Label>
                <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="ground / express" />
              </div>
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <h3 className="font-medium">Ship To</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input required placeholder="Name" value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} />
              <Input placeholder="Company" value={shipTo.company} onChange={(e) => setShipTo({ ...shipTo, company: e.target.value })} />
              <Input required placeholder="Street 1" value={shipTo.street1} onChange={(e) => setShipTo({ ...shipTo, street1: e.target.value })} />
              <Input placeholder="Street 2" value={shipTo.street2} onChange={(e) => setShipTo({ ...shipTo, street2: e.target.value })} />
              <Input required placeholder="City" value={shipTo.city} onChange={(e) => setShipTo({ ...shipTo, city: e.target.value })} />
              <Input required placeholder="State" value={shipTo.state} onChange={(e) => setShipTo({ ...shipTo, state: e.target.value })} />
              <Input required placeholder="Postal code" value={shipTo.postal_code} onChange={(e) => setShipTo({ ...shipTo, postal_code: e.target.value })} />
              <Input required maxLength={2} placeholder="Country (ISO-2)" value={shipTo.country} onChange={(e) => setShipTo({ ...shipTo, country: e.target.value.toUpperCase() })} />
              <Input placeholder="Phone" value={shipTo.phone} onChange={(e) => setShipTo({ ...shipTo, phone: e.target.value })} />
              <Input type="email" placeholder="Email" value={shipTo.email} onChange={(e) => setShipTo({ ...shipTo, email: e.target.value })} />
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Lines</h3>
              <Button type="button" size="sm" variant="outline" onClick={addLine}>
                <Plus className="h-4 w-4 mr-1" />Add line
              </Button>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-end">
                <div className="space-y-1">
                  {i === 0 && <Label className="text-xs">SKU</Label>}
                  <Select value={l.skuId} onValueChange={(v) => updateLine(i, { skuId: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick SKU" /></SelectTrigger>
                    <SelectContent>
                      {data?.skus?.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name ?? ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  {i === 0 && <Label className="text-xs">Qty</Label>}
                  <Input type="number" min={1} max={10000} value={l.qty}
                    onChange={(e) => updateLine(i, { qty: parseInt(e.target.value) || 1 })} />
                </div>
                <Button type="button" size="icon" variant="ghost" disabled={lines.length === 1} onClick={() => removeLine(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </Card>

          <Card className="p-4 space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special handling…" />
          </Card>

          {cfDefs.length > 0 && (
            <Card className="p-4 space-y-3">
              <h3 className="font-medium">Custom Fields</h3>
              <CustomFieldsForm defs={cfDefs} values={customFields} onChange={setCustomFields} />
            </Card>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate({ to: "/portal/orders" })}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit order
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}